import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateBacktest, useStrategy } from '@/api/queries';
import type { DatasetSummaryDto, StrategyDocumentDto, StrategySummaryDto } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import styles from './NewBacktestDialog.module.css';

interface NewBacktestDialogProps {
  datasets: readonly DatasetSummaryDto[];
  strategies: readonly StrategySummaryDto[];
  fixedStrategyId?: string;
  fixedStrategyDocument?: StrategyDocumentDto;
  onClose: () => void;
}

export function NewBacktestDialog({ datasets, strategies, fixedStrategyId, fixedStrategyDocument, onClose }: NewBacktestDialogProps) {
  const navigate = useNavigate();
  const createBacktest = useCreateBacktest();
  const readyDatasets = datasets.filter((dataset) => dataset.status === 'Ready' && dataset.id);
  const [datasetId, setDatasetId] = useState(readyDatasets[0]?.id ?? '');
  const [strategyId, setStrategyId] = useState(fixedStrategyId ?? strategies[0]?.id ?? '');
  const [accountSize, setAccountSize] = useState('10000');
  const [traceLevel, setTraceLevel] = useState('Summary');
  const [error, setError] = useState<string | null>(null);
  const selectedDatasetId = readyDatasets.some((dataset) => dataset.id === datasetId)
    ? datasetId
    : readyDatasets[0]?.id ?? '';
  const selectedStrategyId = fixedStrategyId ?? (strategies.some((strategy) => strategy.id === strategyId)
    ? strategyId
    : strategies[0]?.id ?? '');
  const { data: loadedStrategyDocument, isLoading: strategyLoading } = useStrategy(selectedStrategyId, {
    enabled: !!selectedStrategyId && !fixedStrategyDocument,
  });
  const strategyDocument = fixedStrategyDocument ?? loadedStrategyDocument;

  async function handleSubmit() {
    const parsedAccountSize = Number(accountSize);
    const validationError = validateBacktestForm(
      selectedDatasetId,
      selectedStrategyId,
      strategyDocument,
      parsedAccountSize,
    );
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    try {
      const run = await createBacktest.mutateAsync({
        datasetId: selectedDatasetId,
        strategyDocument: strategyDocument!,
        accountSize: parsedAccountSize,
        traceLevel,
      });
      if (!run.id) throw new Error('The server did not return a run ID.');
      onClose();
      navigate(`/backtests/${encodeURIComponent(run.id)}`);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Could not queue the backtest.');
    }
  }

  return (
    <Dialog
      title="New backtest"
      onClose={onClose}
      actions={(
        <>
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            disabled={!selectedDatasetId || !selectedStrategyId || strategyLoading || createBacktest.isPending}
            onClick={() => void handleSubmit()}
          >
            Queue backtest
          </Button>
        </>
      )}
    >
      <div className={styles.fields}>
        <Select
          label="Dataset"
          value={selectedDatasetId}
          onChange={(event) => setDatasetId(event.target.value)}
          options={readyDatasets.map((dataset) => ({
            value: dataset.id ?? '',
            label: `${dataset.displayName ?? dataset.id} (${dataset.instrument ?? 'unknown'})`,
          }))}
        />
        {fixedStrategyDocument ? (
          <Input label="Strategy" value={fixedStrategyDocument.definition?.name ?? selectedStrategyId} disabled readOnly />
        ) : (
          <Select
            label="Strategy"
            value={selectedStrategyId}
            onChange={(event) => setStrategyId(event.target.value)}
            options={strategies.map((strategy) => ({ value: strategy.id, label: strategy.name }))}
          />
        )}
        <Input
          label="Starting account"
          type="number"
          min="0.01"
          step="0.01"
          value={accountSize}
          onChange={(event) => setAccountSize(event.target.value)}
        />
        <Select
          label="Trace detail"
          value={traceLevel}
          onChange={(event) => setTraceLevel(event.target.value)}
          options={[
            { value: 'Summary', label: 'Summary — decisions and trades' },
            { value: 'Full', label: 'Full — node context at decisions' },
          ]}
        />
        {readyDatasets.length === 0 && (
          <p className="statusText statusTextWarning">Import a ready dataset before creating a backtest.</p>
        )}
        {strategies.length === 0 && (
          <p className="statusText statusTextWarning">Create a saved strategy before creating a backtest.</p>
        )}
        {error && <p className="statusText statusTextDanger">{error}</p>}
      </div>
    </Dialog>
  );
}

export function validateBacktestForm(
  datasetId: string,
  strategyId: string,
  strategyDocument: StrategyDocumentDto | undefined,
  accountSize: number,
) {
  if (!datasetId || !strategyId || !strategyDocument) {
    return 'Choose a ready dataset and saved strategy first.';
  }
  if (!Number.isFinite(accountSize) || accountSize <= 0) {
    return 'Account size must be greater than zero.';
  }
  return null;
}
